import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    createCustomPrice,
    updateCustomPrice,
    deleteCustomPrice,
    getCustomPricesByPatientId,
    getCustomPricesByPatientAndDoctor
} from '../../redux/customPriceSlice';
import emptykey from '../../assets/img/colorkit.svg';
import { Plus, X, Trash2, Edit, ChevronLeft, ChevronRight, Stethoscope, User, DollarSign, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row translated <option> for the specialty picker in CustomPrice.
// The <select> stores the specialtyId as its value, so we only translate the label.
const SpecialtyOption = ({ spec }) => {
    const label = useMasterLabel('masterSpecialty', spec.specialtyId, 'name', spec.name);
    return <option value={spec.specialtyId}>{label}</option>;
};

// Custom-price card heading. Resolves the specialty via its ID so language switches
// update the card without refetching. Accepts the snapshot as fallback.
const PriceCardSpecialtyName = ({ specialityId, fallback }) => {
    const label = useMasterLabel('masterSpecialty', specialityId, 'name', fallback);
    return <>{label}</>;
};

// PORTED FROM THE TENANT APP with one change: patientId and doctorId can be
// handed in.
//
// The tenant version reads the patient off `location.state` and the doctor off
// localStorage, because there is exactly one of each — the hospital you are
// signed in to, and the doctor you are. Super Admin has neither: a Mitra One
// Care identity has one patient row PER HOSPITAL, and no doctor of its own. So
// the caller resolves both (MocCustomPriceTab) and passes them down. Everything
// else — the fetch, the modal, the currency, the specialty labels — is the
// tenant screen unchanged, which is the point.
const CustomPrice = ({ patientId: patientIdProp, doctorId: doctorIdProp } = {}) => {
    const { t } = useTranslation(['customPrice']);
    const location = useLocation();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const { selectedPatient } = useSelector((state) => state.patient);
    const { selectedDoctor } = useSelector((state) => state.doctor);
    const { customPrices, pagination, loading, error } = useSelector((state) => state.customPrice);

    const appointmentSpeciality = selectedDoctor?.appointmentSpeciality || [];
    const userRole = localStorage.getItem('Role');
    const patientId = patientIdProp
        || (userRole === 'admin' || userRole === 'super_admin' || userRole === 'doctor'
            ? (location?.state?.patientId || selectedPatient?.patientId)
            : localStorage.getItem('ID'));
    const doctorId = doctorIdProp || (userRole === 'doctor' ? localStorage.getItem('ID') : null);

    const [formData, setFormData] = useState({
        specialtyId: '',
        appointmentTypeId: '',
        actualPrice: '',
        customPrice: '',
    });

    const [priceError, setPriceError] = useState('');

    // Fetch custom prices when page changes
    useEffect(() => {
        if (patientId) {
            if (doctorId) {
                dispatch(getCustomPricesByPatientAndDoctor({
                    patientId,
                    doctorId,
                    page: currentPage,
                    limit: 5
                }));
            } else {
                dispatch(getCustomPricesByPatientId({
                    patientId,
                    page: currentPage,
                    limit: 5
                }));
            }
        }
    }, [dispatch, patientId, doctorId, currentPage, userRole]);

    const getSpecialtyName = (id) => {
        const spec = appointmentSpeciality.find((s) => s.specialtyId === id);
        return spec ? spec.name : t('customPrice.unknown');
    };

    const getAppointmentTypeName = (id, specialtyId) => {
        const spec = appointmentSpeciality.find((s) => s.specialtyId === specialtyId);
        if (!spec) return t('customPrice.unknown');
        const appt = spec.appointmentTypes?.find((a) => a.typeId === id);
        return appt ? appt.type : t('customPrice.unknown');
    };

    const getCurrencySymbol = () => {
        return selectedDoctor?.country?.currencySymbol || selectedDoctor?.country?.currencyCode || '';
    };

    const formatPrice = (price) => {
        const numericPrice = typeof price === 'number' ? price : parseFloat(price);
        const currencySymbol = getCurrencySymbol();
        return isNaN(numericPrice) ? t('customPrice.na') : `${currencySymbol} ${numericPrice.toFixed(2)}`;
    };

    useEffect(() => {
        if (formData.appointmentTypeId && !isEditing) {
            const selectedSpecialityObj = appointmentSpeciality.find(
                (item) => item.specialtyId === formData.specialtyId
            );
            const selectedAppt = selectedSpecialityObj?.appointmentTypes?.find(
                (appt) => appt.typeId === formData.appointmentTypeId
            );
            if (selectedAppt && selectedAppt.price) {
                setFormData((prev) => ({
                    ...prev,
                    actualPrice: selectedAppt.price.toString(),
                    customPrice: selectedAppt.price.toString(),
                }));
            }
        }
    }, [formData.appointmentTypeId, appointmentSpeciality, isEditing]);

    const handleAddClick = () => {
        setIsEditing(false);
        setEditingItem(null);
        setIsModalOpen(true);
        setPriceError('');
        setFormData({
            specialtyId: '',
            appointmentTypeId: '',
            actualPrice: '',
            customPrice: '',
        });
    };

    const handleEditClick = (item) => {
        setIsEditing(true);
        setEditingItem(item);
        setFormData({
            specialtyId: item.specialityId,
            appointmentTypeId: item.appointmentTypeId,
            actualPrice: item.actualPrice ? item.actualPrice.toString() : '',
            customPrice: item.customPrice.toString(),
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingItem(null);
        setPriceError('');
        setFormData({
            specialtyId: '',
            appointmentTypeId: '',
            actualPrice: '',
            customPrice: '',
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'specialtyId') {
            setFormData((prev) => ({ ...prev, specialtyId: value, appointmentTypeId: '', actualPrice: '', customPrice: '' }));
            setPriceError('');
        } else if (name === 'customPrice') {
            const intValue = value.replace(/[^0-9]/g, '');
            if (intValue === '' || (Number(intValue) >= 0 && Number(intValue) <= 100000)) {
                setFormData((prev) => ({ ...prev, [name]: intValue }));

                // Validate if custom price is different from actual price
                const actualPriceValue = parseFloat(formData.actualPrice);
                const customPriceValue = parseFloat(value);

                if (value !== '' && formData.actualPrice !== '' && customPriceValue === actualPriceValue) {
                    setPriceError(t('customPrice.same_price_error'));
                } else {
                    setPriceError('');
                }
            }
        } else if (name === 'actualPrice') {
            if (value === '' || (Number(value) >= 0 && Number(value) <= 100000)) {
                setFormData((prev) => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const specialtyId = formData.specialtyId;
        const appointmentTypeId = formData.appointmentTypeId;
        const actualPriceValue = parseFloat(formData.actualPrice);
        const customPriceValue = parseFloat(formData.customPrice);

        // Validate that custom price is different from actual price
        if (customPriceValue === actualPriceValue) {
            setPriceError(t('customPrice.same_price_error'));
            return;
        }

        if (customPriceValue > 100000 || customPriceValue < 0 || actualPriceValue > 100000 || actualPriceValue < 0) {
            toast.warn(t('customPrice.max_price_error'));
            return;
        }

        const selectedSpecialityObj = appointmentSpeciality.find(
            (item) => item.specialtyId === specialtyId
        );
        const selectedAppt = selectedSpecialityObj?.appointmentTypes?.find(
            (appt) => appt.typeId === appointmentTypeId
        );

        Swal.fire({
            title: isEditing
                ? t('customPrice.update_confirmation_title')
                : t('customPrice.add_confirmation_title'),
            text: isEditing
                ? t('customPrice.update_confirmation_text')
                : t('customPrice.add_confirmation_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#ef4444',
            confirmButtonText: isEditing
                ? t('customPrice.update')
                : t('customPrice.save'),
            cancelButtonText: t('customPrice.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (isEditing) {
                        // Update existing custom price
                        const updatePayload = {
                            actualPrice: actualPriceValue,
                            customPrice: customPriceValue,
                            specialityName: selectedSpecialityObj?.name || editingItem.specialityName,
                            appointmentTypeName: selectedAppt?.type || editingItem.appointmentTypeName,
                        };

                        await dispatch(updateCustomPrice({
                            uniqueId: editingItem.uniqueId,
                            payload: updatePayload
                        })).unwrap();

                        toast.success(t('customPrice.update_success_text'));
                    } else {
                        // Create new custom price
                        const createPayload = {
                            doctorId: doctorId || selectedPatient?.doctorId || '',
                            patientId: patientId,
                            specialityId: specialtyId,
                            specialityName: selectedSpecialityObj?.name || t('customPrice.unknown'),
                            appointmentTypeId: appointmentTypeId,
                            appointmentTypeName: selectedAppt?.type || t('customPrice.unknown'),
                            actualPrice: actualPriceValue,
                            customPrice: customPriceValue,
                            status: 'active',
                        };

                        await dispatch(createCustomPrice(createPayload)).unwrap();

                        toast.success(t('customPrice.add_success_text'));
                    }

                    // Refresh the list
                    if (doctorId) {
                        dispatch(getCustomPricesByPatientAndDoctor({
                            patientId,
                            doctorId,
                            page: currentPage,
                            limit: 5
                        }));
                    } else {
                        dispatch(getCustomPricesByPatientId({
                            patientId,
                            page: currentPage,
                            limit: 5
                        }));
                    }

                    handleCloseModal();
                } catch (err) {
                    const errorMessage = err?.message?.en || err?.message || t('customPrice.error_text');
                    toast.warn(errorMessage);
                }
            }
        });
    };

    const handleDelete = async (uniqueId) => {
        Swal.fire({
            title: t('customPrice.delete_confirmation_title'),
            text: t('customPrice.delete_confirmation_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('customPrice.delete'),
            cancelButtonText: t('customPrice.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await dispatch(deleteCustomPrice(uniqueId)).unwrap();

                    // Refresh the list
                    if (doctorId) {
                        dispatch(getCustomPricesByPatientAndDoctor({
                            patientId,
                            doctorId,
                            page: currentPage,
                            limit: 5
                        }));
                    } else {
                        dispatch(getCustomPricesByPatientId({
                            patientId,
                            page: currentPage,
                            limit: 5
                        }));
                    }

                    toast.success(t('customPrice.delete_success_text'));
                } catch (err) {
                    const errorMessage = err?.message?.en || err?.message || t('customPrice.delete_error_text');
                    toast.warn(errorMessage);
                }
            }
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const selectedSpecialityObj = appointmentSpeciality.find(
        (item) => item.specialtyId === formData.specialtyId
    );

    return (
        <div data-testid="custom-price-container" className="mt-4">
            <div data-testid="custom-price-header" className="flex flex-col sm:flex-row flex-wrap gap-4 justify-between items-center mb-6">
                <h2 data-testid="custom-price-heading" className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('customPrice.heading')}
                </h2>
                {userRole === 'doctor' && (
                    <button
                        data-testid="custom-price-add-button"
                        id="patient-profile-additional-input-add-price"
                        onClick={handleAddClick}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer w-full sm:w-auto justify-center"
                    >
                        <Plus className="w-5 h-5" />
                        <span className="relative z-10">{t('customPrice.add_price')}</span>
                    </button>
                )}
            </div>

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    id="custom-price-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="custom-price-modal-title"
                                data-testid="modal-title"
                            >
                                {isEditing ? t('customPrice.edit_custom_price') : t('customPrice.add_custom_price')}
                            </h3>
                            <button
                                id="custom-price-modal-close-button"
                                onClick={handleCloseModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="custom-price-form" onSubmit={handleSubmit} data-testid="custom-price-form">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-specialty-input"
                                            id="custom-price-specialty-label"
                                            data-testid="specialty-label"
                                        >
                                            {t('customPrice.specialty_name')} <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="custom-price-specialty-input"
                                            name="specialtyId"
                                            value={formData.specialtyId}
                                            onChange={handleInputChange}
                                            className="tt-select w-full p-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300"
                                            required
                                            disabled={isEditing}
                                            data-testid="specialty-input"
                                        >
                                            <option value="">{t('customPrice.select_specialty')}</option>
                                            {appointmentSpeciality.map((spec) => (
                                                <SpecialtyOption key={spec.specialtyId} spec={spec} />
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-appointment-type-input"
                                            id="custom-price-appointment-type-label"
                                            data-testid="appointment-type-label"
                                        >
                                            {t('customPrice.appointment_type')} <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="custom-price-appointment-type-input"
                                            name="appointmentTypeId"
                                            value={formData.appointmentTypeId}
                                            onChange={handleInputChange}
                                            className="tt-select w-full p-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300"
                                            required
                                            disabled={!formData.specialtyId || isEditing}
                                            data-testid="appointment-type-input"
                                        >
                                            <option value="">{t('customPrice.select_type')}</option>
                                            {selectedSpecialityObj?.appointmentTypes?.map((appt) => (
                                                <option key={appt.typeId} value={appt.typeId}>
                                                    {appt.type}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-actual-price-input"
                                            id="custom-price-actual-price-label"
                                            data-testid="actual-price-label"
                                        >
                                            {t('customPrice.actual_price_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="custom-price-actual-price-input"
                                            type="number"
                                            name="actualPrice"
                                            value={formData.actualPrice}
                                            onChange={handleInputChange}
                                            className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed text-sm transition-all duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            required
                                            min="0"
                                            max="100000"
                                            step="0.01"
                                            disabled
                                            readOnly
                                            data-testid="actual-price-input"
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-price-input"
                                            id="custom-price-price-label"
                                            data-testid="price-label"
                                        >
                                            {t('customPrice.price_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="custom-price-price-input"
                                            type="number"
                                            name="customPrice"
                                            value={formData.customPrice}
                                            onChange={handleInputChange}
                                            onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 text-sm transition-all duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${priceError
                                                ? 'border-red-500 focus:ring-red-500 hover:border-red-500'
                                                : 'border-gray-300 focus:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                                }`}
                                            required
                                            min="0"
                                            max="100000"
                                            step="1"
                                            data-testid="price-input"
                                        />
                                        {priceError && (
                                            <p className="mt-2 text-sm text-red-600 font-medium">
                                                {priceError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="custom-price-form-cancel-button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={loading}
                                data-testid="form-cancel-button"
                            >
                                {t('customPrice.cancel')}
                            </button>
                            <button
                                type="submit"
                                id="custom-price-form-submit-button"
                                form="custom-price-form"
                                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                disabled={loading || !!priceError}
                                data-testid="form-submit-button"
                            >
                                {loading && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {loading
                                    ? t('customPrice.loading')
                                    : isEditing
                                        ? t('customPrice.update')
                                        : t('customPrice.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && customPrices.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[200px] px-4 sm:px-6 lg:px-8">
                    <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4">
                        {t('customPrice.loading')}
                    </p>
                </div>
            ) : customPrices.length === 0 ? (
                <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                    <img
                        src={emptykey}
                        alt={t('customPrice.no_custom_price_found')}
                        className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                    />
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                        {t('customPrice.empty')}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {customPrices.map((price, index) => (
                            <div
                                key={price.uniqueId || index}
                                className="group relative rounded-2xl border border-gray-200 bg-white shadow-md hover:shadow-xl hover:shadow-[#4ABDBD]/20 transition-all duration-300 overflow-hidden hover:-translate-y-1"
                                data-testid={`custom-price-card-${index}`}
                            >
                                {/* Header with Doctor Name */}
                                <div className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] p-3 sm:p-4">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                                                <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                            </div>
                                            <h3 className="text-sm sm:text-base font-bold text-white truncate">
                                                {price.doctorName || '-'}
                                            </h3>
                                        </div>

                                        {/* Action Buttons */}
                                        {userRole === 'doctor' && (
                                            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                                                <button
                                                    data-testid={`custom-price-edit-button-${price.uniqueId || index}`}
                                                    id={`patient-profile-additional-input-edit-${index}`}
                                                    onClick={() => handleEditClick(price)}
                                                    className="group/btn p-1.5 sm:p-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-200 cursor-pointer"
                                                    title={t('customPrice.edit')}
                                                >
                                                    <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                                                </button>
                                                <button
                                                    data-testid={`custom-price-delete-button-${price.uniqueId || index}`}
                                                    id={`patient-profile-additional-input-delete-${index}`}
                                                    onClick={() => handleDelete(price.uniqueId)}
                                                    className="group/btn p-1.5 sm:p-2 rounded-lg bg-white/20 hover:bg-red-500/80 backdrop-blur-sm transition-all duration-200 cursor-pointer"
                                                    title={t('customPrice.delete_title')}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                                    {/* Specialty Name and Appointment Type - Responsive Row/Column */}
                                    <div className="flex flex-col md:flex-row md:gap-4 gap-2.5">
                                        {/* Specialty Name */}
                                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                            <div className="mt-0.5 flex-shrink-0">
                                                <Stethoscope className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#4ABDBD]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium">
                                                    {t("customPrice.specialty_name")}
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    <PriceCardSpecialtyName specialityId={price.specialityId} fallback={price.specialityName || getSpecialtyName(price.specialityId)} />
                                                </p>
                                            </div>
                                        </div>

                                        {/* Appointment Type */}
                                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                            <div className="mt-0.5 flex-shrink-0">
                                                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#4ABDBD]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium">
                                                    {t("customPrice.type_label")}
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    {price.appointmentTypeName ||
                                                        getAppointmentTypeName(price.appointmentTypeId, price.specialityId)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200 my-2.5 sm:my-3"></div>

                                    {/* Prices Section */}
                                    <div className="space-y-2">
                                        {/* Actual Price */}
                                        <div className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg">
                                            <span className="text-xs sm:text-sm text-gray-600 font-medium">
                                                {t("customPrice.actual_price_label")}
                                            </span>
                                            <span className="text-sm sm:text-base font-bold text-gray-500 line-through">
                                                {formatPrice(price.actualPrice)}
                                            </span>
                                        </div>

                                        {/* Custom Price */}
                                        <div className={`flex items-center justify-between p-2.5 sm:p-3 rounded-lg ${price.value === 'increase'
                                            ? 'bg-green-50 border border-green-200'
                                            : 'bg-red-50 border border-red-200'
                                            }`}>
                                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                                {price.value === 'increase' ? (
                                                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                                                ) : (
                                                    <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                                                )}
                                                <span className={`text-xs sm:text-sm font-semibold truncate ${price.value === 'increase' ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                    {t("customPrice.price_display_label")}
                                                </span>
                                            </div>
                                            <span className={`text-base sm:text-lg font-bold flex-shrink-0 ml-2 ${price.value === 'increase' ? 'text-green-700' : 'text-red-700'
                                                }`}>
                                                {formatPrice(price.customPrice)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom accent */}
                                <div className={`h-1 ${price.value === 'increase'
                                    ? 'bg-gradient-to-r from-green-400 to-green-600'
                                    : 'bg-gradient-to-r from-red-400 to-red-600'
                                    }`}></div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div data-testid="custom-price-pagination" className="flex items-center justify-center gap-2 mt-6">
                            <button
                                data-testid="custom-price-prev-page-button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || loading}
                                className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] text-gray-700 hover:text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-gray-100 disabled:hover:to-gray-200 disabled:hover:text-gray-700"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>

                            <div className="flex items-center gap-2">
                                <span data-testid="custom-price-page-indicator" className="text-sm font-semibold text-gray-700">
                                    {t('customPrice.page')} {currentPage} {t('customPrice.of')} {pagination.totalPages}
                                </span>
                            </div>

                            <button
                                data-testid="custom-price-next-page-button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages || loading}
                                className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] text-gray-700 hover:text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-gray-100 disabled:hover:to-gray-200 disabled:hover:text-gray-700"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CustomPrice;
